<template>
  <div>
    <template v-if="consignment.status === 'SUCCESS'">
      <div class="mb-2 d-flex align-items-center justify-content-between shipment-page-header">
        <router-link
          :to="{ name: 'consignments' }"
          class="d-flex align-items-center gap-2 text-primary font-weight-bold"
        >
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-1" />
          {{ t('Consignments') }}
        </router-link>

        <div class="d-flex align-items-center">
          <b-button
            variant="primary"
            size="sm"
            class="d-flex align-items-center justify-content-center mr-50"
            :disabled="downloadLabelSubmitting"
            @click="downloadDocumentLabels(consignment.data.labels)"
          >
            <feather-icon icon="PrinterIcon" size="16" class="mr-50" />
            {{ downloadLabelSubmitting ? t('Please Wait') : t('Print Shipment Labels') }}
          </b-button>
          <b-dropdown
            right
            variant="outline-primary"
            size="sm"
            class="shipment-actions-dropdown"
            toggle-class="p-50 d-flex align-items-center"
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" size="18" />
            </template>
            <b-dropdown-item @click="copyConsignment(consignment.data.id)">
              <feather-icon icon="CopyIcon" size="16" class="mr-50" />
              {{ t('Copy Shipment') }}
            </b-dropdown-item>
            <b-dropdown-item @click="sendToProviderWithConfirmation(consignment.data.id)">
              <feather-icon icon="SendIcon" size="16" class="mr-50" />
              {{ t('Send to Provider') }}
            </b-dropdown-item>
            <b-dropdown-item @click="updateStatusWithConfirmation(consignment.data.id)">
              <feather-icon icon="RefreshCwIcon" size="16" class="mr-50" />
              {{ t('Update Status') }}
            </b-dropdown-item>
            <b-dropdown-item @click="updatePackageWithConfirmation(consignment.data.id)">
              <feather-icon icon="MaximizeIcon" size="16" class="mr-50" />
              {{ t('Update Package') }}
            </b-dropdown-item>
            <b-dropdown-item
              v-if="consignment.data.consignmentType === 2"
              @click="formalizeShipmentConfirm(consignment.data.id)"
            >
              <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
              {{ t('Formalize Shipment') }}
            </b-dropdown-item>
            <b-dropdown-item @click="createSpecialOffer(consignment.data.id)">
              <feather-icon icon="FileTextIcon" size="16" class="mr-50" />
              {{ t('Create Special Offer') }}
            </b-dropdown-item>
            <b-dropdown-item @click="cancelConsignment(consignment.data.id)" class="text-danger">
              <feather-icon icon="XCircleIcon" size="16" class="mr-50 text-danger" />
              <span class="text-danger">{{ t('Cancel Consignment') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>

      <b-modal
        id="create-consignment-modal"
        ref="create-consignment-modal"
        size="lg"
        :title="t('Create Refund')"
        :ok-title="t('Make Refund')"
        @ok="createReturnConsignment"
        @hide="handleCancel"
      >
        <b-card no-body>
          <b-card-body>
            <consignment-refund-packages
              :data="consignment.data.packages"
              @returnPackages="handleReturnPackages"
            />
          </b-card-body>
        </b-card>
        <b-row>
          <b-col lg="6">
            <address-information :title="t('Consignor')" :data="consignment.data.consignor" />
          </b-col>
          <b-col lg="6">
            <address-information :title="t('Consignee')" :data="consignment.data.consignee" />
          </b-col>
        </b-row>
      </b-modal>
      <!-- Top summary section -->
      <b-row>
        <b-col cols="12">
          <b-card no-body class="shipment-summary-card mb-1">
            <b-card-body>
              <div class="d-flex flex-column flex-lg-row justify-content-between">
                <!-- Left side: status and main information -->
                <div class="mb-1 mb-lg-0 w-100">
                  <b-row class="shipment-main-info-row">
                    <b-col md="4" sm="12" class="mb-50">
                      <div class="d-flex align-items-center">
                        <span class="detail-label mr-50">{{ t('Status') }}:</span>
                        <b-badge pill variant="light-success" class="status-badge">
                          {{ consignment.data.status_message }}
                        </b-badge>
                      </div>
                    </b-col>
                    <b-col md="4" sm="6" class="mb-50">
                      <div class="detail-label">
                        {{ t('Shipment Acceptance Date') }}
                      </div>
                      <div class="detail-value">
                        {{ formatDateToCustom(consignment.data.drop_off_event_at) }}
                      </div>
                    </b-col>
                    <b-col md="4" sm="6" class="mb-50">
                      <div class="detail-label">
                        {{ t('Desi') }}
                      </div>
                      <div class="detail-value">
                        {{ consignment.data.desi }}
                      </div>
                    </b-col>
                  </b-row>

                  <b-row class="shipment-main-info-row">
                    <b-col md="4" sm="12" class="mb-50">
                      <div class="detail-label">{{ t('Reference No') }}</div>
                      <div class="detail-value">{{ consignment.data.reference_no || '-' }}</div>
                    </b-col>
                    <b-col md="4" sm="6" class="mb-50">
                      <div class="detail-label">
                        {{ t('Shipment Creation Date') }}
                      </div>
                      <div class="detail-value">
                        {{ formatDateToCustom(consignment.data.created_at) }}
                      </div>
                    </b-col>
                    <b-col md="4" sm="6" class="mb-50">
                      <div class="detail-label">
                        {{ t('Weight') }}
                      </div>
                      <div class="detail-value">{{ consignment.data.weight }} Kg</div>
                    </b-col>
                  </b-row>

                  <div
                    v-if="consignment.data.labels && consignment.data.labels.length"
                    class="d-flex flex-column flex-md-row align-items-md-center mt-1 provider-tracking-row"
                  >
                    <div v-if="consignment.data.ioss_number" class="d-flex align-items-center mr-1">
                      <div class="detail-label mr-50">{{ t('IOSS Number') }}:</div>
                      <div class="detail-value">
                        {{ consignment.data.ioss_number }}
                      </div>
                    </div>
                    <div class="detail-label mr-md-1 mb-25 mb-md-0">
                      {{ consignment.data.provider.name }} {{ t('Tracking Number') }}:
                    </div>
                    <div class="d-flex flex-wrap align-items-center">
                      <div
                        v-for="(label, index) in trackingLabelsToShow"
                        :key="index"
                        class="tracking-badge-wrapper mr-25 mb-25 cursor-pointer"
                        @click="copyTrackingCode(label.tracking_code)"
                      >
                        <b-badge
                          pill
                          variant="light-primary"
                          class="tracking-chip d-inline-flex align-items-center"
                        >
                          <span>{{ label.tracking_code }}</span>
                          <feather-icon icon="CopyIcon" size="14" class="ml-25 text-primary" />
                        </b-badge>
                      </div>

                      <span
                        v-if="consignment.data.labels.length > 3"
                        class="see-all-link font-small-3"
                      >
                        {{ t('Show All') }} ({{ consignment.data.labels.length }})
                      </span>
                    </div>
                  </div>
                </div>

                <!-- Right side column intentionally left empty; actions are in the top header -->
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
      <b-row>
        <b-col cols="12">
          <ConsignmentDocuments
            :documents="consignment.data.documents"
            :consignment-id="consignment.data.id"
            :permissions="consignment.data.permissions"
            @reloadData="getData"
          />

          <b-row>
            <b-col lg="6">
              <address-information :title="t('Consignor')" :data="consignment.data.consignor" />
            </b-col>
            <b-col lg="6">
              <address-information :title="t('Consignee')" :data="consignment.data.consignee" />
            </b-col>
          </b-row>

          <b-card no-body>
            <b-card-body>
              <div
                v-if="consignment.data.packages && consignment.data.packages.length"
                class="package-tabs-wrapper mb-1"
              >
                <div class="package-tabs-scroll">
                  <button
                    v-for="(pkg, index) in consignment.data.packages"
                    :key="index"
                    type="button"
                    class="package-tab"
                    :class="{ 'package-tab--active': activePackageIndex === index }"
                    @click="activePackageIndex = index"
                  >
                    <span class="package-tab-icon d-flex align-items-center justify-content-center">
                      <feather-icon :icon="getPackageIcon(pkg.package_type)" size="14" />
                    </span>
                    <span class="package-tab-label"> {{ t('Package') }} {{ index + 1 }} </span>
                    <span v-if="getPackageStatusText(pkg)" class="package-status-chip ml-50">
                      {{ getPackageStatusText(pkg) }}
                    </span>
                  </button>
                </div>
              </div>
              <h4 class="mb-1 font-weight-bold">
                {{ t('Package Informations') }}
              </h4>

              <div v-if="currentPackage" class="mt-1">
                <b-row class="package-info-row mb-1">
                  <b-col md="3" sm="6" class="mb-50">
                    <div class="detail-label">
                      {{ consignment.data.provider.name }} {{ t('Tracking Number') }}
                    </div>
                    <div class="detail-value">
                      {{ getPackageTrackingNumber(currentPackage.id) || '-' }}
                    </div>
                  </b-col>
                  <b-col md="2" sm="6" class="mb-50">
                    <div class="detail-label">
                      {{ t('Weight') }}
                    </div>
                    <div class="detail-value">{{ currentPackage.weight }} Kg</div>
                  </b-col>
                  <b-col md="3" sm="6" class="mb-50">
                    <div class="detail-label">
                      {{ t('Dimensions') }}
                    </div>
                    <div class="detail-value">
                      {{ currentPackage.length }} X {{ currentPackage.width }} X
                      {{ currentPackage.height }}
                    </div>
                  </b-col>
                  <b-col md="2" sm="6" class="mb-50">
                    <div class="detail-label">
                      {{ t('Package Type') }}
                    </div>
                    <div class="detail-value">
                      {{ getPackageTypeName(currentPackage.package_type) }}
                    </div>
                  </b-col>
                  <b-col md="2" sm="6" class="mb-50">
                    <div class="detail-label">
                      {{ t('Post Type') }}
                    </div>
                    <div class="detail-value">
                      {{ getPostTypeName(currentPackage.post_type) }}
                    </div>
                  </b-col>
                </b-row>

                <div class="package-items-table mt-1">
                  <div class="table-responsive mb-0">
                    <table class="w-100 table table-sm package-items-table-inner">
                      <thead>
                        <tr>
                          <th class="text-left">
                            {{ t('Description') }}
                          </th>
                          <th class="text-center">
                            {{ t('Gtip Code') }}
                          </th>
                          <th class="text-center">
                            {{ t('Quantity') }}
                          </th>
                          <th class="text-right">
                            {{ t('Unit Price') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, index) in currentPackage.items" :key="index">
                          <td class="text-left">
                            <div>{{ item.description }}</div>
                            <div
                              v-if="
                                item.merchant_product_id ||
                                item.manufacturer_product_id ||
                                item.standard_product_id
                              "
                              class="mt-25 d-flex flex-wrap"
                              style="gap: 4px"
                            >
                              <b-badge
                                v-if="item.merchant_product_id"
                                variant="light-primary"
                                class="font-small-1"
                              >
                                M-PID: {{ item.merchant_product_id }}
                              </b-badge>
                              <b-badge
                                v-if="item.manufacturer_product_id"
                                variant="light-info"
                                class="font-small-1"
                              >
                                NS-PID: {{ item.manufacturer_product_id }}
                              </b-badge>
                              <b-badge
                                v-if="item.standard_product_id"
                                variant="light-secondary"
                                class="font-small-1"
                              >
                                S-PID: {{ item.standard_product_id }}
                              </b-badge>
                            </div>
                          </td>
                          <td class="text-center">
                            {{ item.gtip || '-' }}
                          </td>
                          <td class="text-center">
                            {{ item.quantity }}
                          </td>
                          <td class="text-right">{{ item.unit_price }} ({{ item.currency }})</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-card>

          <!-- Cargo Movements -->
          <b-card no-body class="mt-1">
            <b-card-body>
              <h4 class="mb-1 font-weight-bold">
                {{ t('Movements') }}
              </h4>

              <div v-if="sortedMovements.length">
                <div
                  v-for="(movement, index) in limitedMovements"
                  :key="index"
                  class="d-flex align-items-center mb-50 movement-row"
                >
                  <feather-icon icon="ClockIcon" size="16" class="mr-50 text-muted" />
                  <div class="flex-grow-1 d-flex">
                    <span class="movement-date font-small-3">
                      {{ formatDateToCustom(movement.status_datetime) }}
                    </span>
                    <span class="movement-message font-weight-bold">
                      {{ movement.message }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted font-small-3">
                {{ t('No records found') }}
              </div>

              <b-button
                v-if="sortedMovements.length"
                block
                variant="outline-primary"
                class="mt-75"
                @click="showMovementsModal = true"
              >
                {{ t('View All Movements') }}
              </b-button>

              <!-- Movements modal -->
              <b-modal
                id="movements-modal"
                ref="movements-modal"
                v-model="showMovementsModal"
                :title="t('All Cargo Movements')"
                hide-footer
                size="lg"
              >
                <div class="table-responsive">
                  <table class="table table-bordered-layout">
                    <thead>
                      <tr>
                        <th>{{ t('Date') }}</th>
                        <th>{{ t('Status') }}</th>
                        <th>{{ t('City') }}</th>
                      </tr>
                    </thead>
                    <tbody class="font-small-3">
                      <tr v-for="(movement, index) in sortedMovements" :key="index">
                        <td>{{ formatDateToCustom(movement.status_datetime) }}</td>
                        <td>{{ movement.message }}</td>
                        <td>{{ getMovementCity(movement.location) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </b-modal>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

      <!-- Additional services section -->
      <b-row class="mt-1">
        <b-col cols="12">
          <b-card no-body>
            <b-card-body>
              <h4 class="mb-1 font-weight-bold">
                {{ $t('Additional Services') }}
              </h4>
              <div
                v-if="consignment.data.special_services && consignment.data.special_services.length"
              >
                <b-row>
                  <b-col
                    v-for="(service, index) in consignment.data.special_services"
                    :key="index"
                    md="4"
                    class="mb-1"
                  >
                    <div class="additional-service-card d-flex">
                      <div class="d-flex align-items-start align-items-center">
                        <div class="additional-service-icon-wrapper">
                          <feather-icon
                            icon="PackageIcon"
                            size="20"
                            class="additional-service-icon"
                          />
                        </div>
                        <div class="flex-grow-1 ml-1">
                          <div class="additional-service-name">
                            {{ service.service_description || '-' }}
                          </div>
                          <div
                            v-if="
                              service.details &&
                              typeof service.details === 'object' &&
                              !Array.isArray(service.details) &&
                              (service.details.accountNumber || service.details.amount)
                            "
                            class="additional-service-details mt-50 d-flex"
                          >
                            <div
                              v-if="service.details.accountNumber"
                              class="additional-service-detail-item mr-1"
                            >
                              {{ t('Postal Check Account') }}: {{ service.details.accountNumber }}
                            </div>
                            <div
                              v-if="service.details.amount"
                              class="additional-service-detail-item"
                            >
                              {{ t('Fee') }}: {{ service.details.amount }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </b-col>
                </b-row>
              </div>
              <div v-else class="text-muted font-small-3 mt-50">
                {{ $t('No Additional Services') }}
              </div>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </template>

    <PageLoading v-else-if="consignment.status === 'LOADING'" />

    <PageError
      v-else-if="consignment.status === 'ERROR'"
      :message="consignment.message"
      :title="t('Error')"
    />
  </div>
</template>

<script>
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import PageLoading from '@core/components/PageLoading.vue';
import PageError from '@core/components/PageError.vue';
import { mapGetters } from 'vuex';
import { helper } from '@core/mixins/helper';
import {
  BRow,
  BCol,
  BCard,
  BCardBody,
  BButton,
  BDropdown,
  BDropdownItem,
  BBadge
} from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import useService from '@/auth/service/useService';
import ConsignmentDocuments from '@/views/pages/consignments/ConsignmentDocuments.vue';
import AddressInformation from '@/components/AddressInformation.vue';
import ConsignmentRefundPackages from '@/components/ConsigmentRefundPackages.vue';

export default {
  components: {
    PageError,
    PageLoading,
    ConsignmentDocuments,
    AddressInformation,
    ConsignmentRefundPackages,

    BRow,
    BCol,
    BCard,
    BCardBody,
    BButton,
    BDropdown,
    BDropdownItem,
    BBadge
  },
  mixins: [helper],

  data() {
    return {
      consignment: {
        data: null,
        status: 'LOADING',
        message: ''
      },
      responseData: {},
      downloadLabelSubmitting: false,
      returnPackagesValue: [],
      activePackageIndex: 0,
      showMovementsModal: false
    };
  },
  computed: {
    ...mapGetters({
      returnItems: 'app/returnItems'
    }),
    currentPackage() {
      if (
        !this.consignment.data ||
        !this.consignment.data.packages ||
        !this.consignment.data.packages.length
      ) {
        return null;
      }

      const index = this.activePackageIndex || 0;
      return this.consignment.data.packages[index] || this.consignment.data.packages[0];
    },
    trackingLabelsToShow() {
      if (!this.consignment.data || !this.consignment.data.labels) return [];
      const labels = this.consignment.data.labels;
      if (labels.length <= 3) return labels;
      return labels.slice(0, 3);
    },
    sortedMovements() {
      if (!this.consignment.data || !this.consignment.data.details) return [];
      return [...this.consignment.data.details].sort((a, b) => {
        const da = new Date(a.status_datetime.replace(' ', 'T'));
        const db = new Date(b.status_datetime.replace(' ', 'T'));
        return db - da; // latest first
      });
    },
    limitedMovements() {
      if (!this.sortedMovements.length) return [];
      return this.sortedMovements.slice(0, 3);
    }
  },

  created() {
    this.getData();
  },
  methods: {
    goBackToConsignments() {
      this.$router.push({ name: 'consignments' });
    },
    getPackageIcon(type) {
      switch (Number(type)) {
        case 1:
          return 'BoxIcon';
        case 2:
          return 'MailIcon';
        case 6:
          return 'FileTextIcon';
        default:
          return 'PackageIcon';
      }
    },
    getPackageStatusText(pkg) {
      if (!pkg) return '';
      return pkg.current_status_message || pkg.status_message || '';
    },
    copyTrackingCode(code) {
      if (!code) return;

      const onSuccess = () => {
        this.showSuccessMessage(this.$t('Copied to clipboard'));
      };

      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(code)
          .then(onSuccess)
          .catch(() => {
            // Fallback below
            const input = document.createElement('input');
            input.value = code;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            document.body.removeChild(input);
            onSuccess();
          });
      } else {
        const input = document.createElement('input');
        input.value = code;
        document.body.appendChild(input);
        input.select();
        document.execCommand('copy');
        document.body.removeChild(input);
        onSuccess();
      }
    },
    getMovementCity(location) {
      if (!location) return '-';
      // Example format: "SULTANGAZI PDM/SULTANGAZI PDM"
      const parts = String(location).split('/');
      const last = parts[parts.length - 1].trim();
      return last || location;
    },
    copyConsignment(id) {
      if (!id) return;
      this.$router.push({
        name: 'create_consignment',
        query: { copy_from: id }
      });
    },

    getPackageTrackingNumber(packageId) {
      if (!this.consignment.data || !this.consignment.data.labels) return '';
      const label = this.consignment.data.labels.find(
        l => String(l.package_id) === String(packageId)
      );
      return label ? label.tracking_code : '';
    },

    async sendToProviderWithConfirmation(consignmentId) {
      const result = await this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Are you sure you want to send the consignment to the provider?')}`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      });

      if (result.value) {
        try {
          const response = await useService.startProcessManually(consignmentId);
          this.showSuccessMessage(
            `${response.message || this.$t('Sending to provider successful')}`
          );
        } catch (error) {
          this.showErrorMessage(`${error.message || this.$t('Sending to provider failed')}`);
        }
      }
    },

    async updateStatusWithConfirmation(consignmentId) {
      const result = await this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Are you sure you want to update the shipment status?')}`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      });

      if (result.value) {
        try {
          const response = await useService.recheckStatus(consignmentId);
          this.showSuccessMessage(
            `${response.message || this.$t('Shipment status updated successfully')}`
          );
          this.getData();
        } catch (error) {
          this.showErrorMessage(
            `${error.message || this.$t('Shipping status could not be updated')}`
          );
        }
      }
    },

    async updatePackageWithConfirmation(consignmentId) {
      const result = await this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Are you sure you want to update the package?')}`,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      });

      if (result.value) {
        try {
          const response = await useService.updatePackageManually(consignmentId);
          this.showSuccessMessage(`${response.message || this.$t('Package updated successfully')}`);
          this.getData();
        } catch (error) {
          this.showErrorMessage(`${error.message || this.$t('Package could not be updated')}`);
        }
      }
    },

    getData() {
      const consignmentId = this.$route.params.id;

      this.consignment = {
        data: null,
        status: 'LOADING',
        message: ''
      };

      useService
        .getConsignmentDetails(consignmentId)
        .then(response => {
          this.responseData = response.data;
          this.consignment = {
            data: response.data,
            status: 'SUCCESS',
            message: ''
          };

          if (this.consignment.data && this.consignment.data.packages) {
            this.activePackageIndex = 0;
          }
        })
        .catch(messages => {
          if (typeof messages === 'object' && messages && messages.length) {
            this.consignment = {
              data: null,
              status: 'ERROR',
              message: messages.map(item => item.message).join(', ')
            };
          }
        });
    },

    downloadDocumentLabels(labels) {
      if (!labels || labels.length === 0) {
        return;
      }

      this.downloadLabelSubmitting = true;

      labels.forEach((label, index) => {
        useService
          .downloadLabelDocument(label.file_url)
          .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', label.file_name);
            document.body.appendChild(link);
            link.click();
          })
          .finally(() => {
            if (index === labels.length - 1) {
              this.downloadLabelSubmitting = false;
            }
          });
      });
    },

    handleCancel() {
      this.$store.commit('app/SET_RETURN_ITEMS', []);
    },

    handleReturnPackages(val) {
      this.returnPackagesValue = val;
      this.returnPackagesValue.forEach(obj => {
        delete obj.dangerous_goods;
        delete obj.id;
        delete obj.desi;
      });
    },

    returnConsignmentModal() {
      this.$refs['create-consignment-modal'].show();
    },

    createReturnConsignment() {
      const { id } = this.$route.params;

      if (!this.returnPackagesValue || this.returnPackagesValue.length < 1) {
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: `${this.$t('Error')}`,
            icon: 'XIcon',
            variant: 'danger',
            text: `${this.$t('No product to return was found.')}`
          }
        });
        return;
      }

      const returnConsignor = this.responseData.consignee;
      const phone_number_consignor = this.responseData.consignee.phone;
      delete returnConsignor.phone;
      returnConsignor.phone_number = phone_number_consignor;

      const returnConsignee = this.responseData.consignor;
      const phone_number_consignee = this.responseData.consignor.phone;
      delete returnConsignee.phone;
      returnConsignee.phone_number = phone_number_consignee;

      const requestData = {
        consignor: {
          ...returnConsignor
        },
        consignee: {
          ...returnConsignee
        },
        packages: this.returnPackagesValue
      };

      useService
        .returnConsignment(id, requestData)
        .then(response => {
          if (response.status) {
            this.showSuccessMessage(response.message);
          }
        })
        .catch(error => {
          error.forEach(x => {
            this.showErrorMessage(x.message);
          });
        });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
<style scoped>
.pdf-container {
  max-width: 100%;
  overflow: auto;
  height: 400px;
}

@media (max-width: 768px) {
  .pdf-container {
    height: 250px;
  }
}

.shipment-summary-card {
  border-radius: 10px;
}

.shipment-main-info-row {
  margin-bottom: 0.25rem;
}

.status-badge {
  padding: 4px 10px;
  font-size: 0.75rem;
}

.detail-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #6e6b7b;
}

.dark-layout .detail-label {
  color: #b4b7bd;
}

.detail-value {
  font-size: 0.95rem;
  font-weight: 600;
  color: #1e1e2d;
}

.dark-layout .detail-value {
  color: #ffffff;
}

.shipment-header-actions .btn {
  min-width: 190px;
}

.shipment-actions-row .btn {
  min-width: auto;
}

.shipment-actions-dropdown ::v-deep .dropdown-toggle {
  border-radius: 8;
  border: 1px solid #e5e7f1;
}

.shipment-actions-dropdown ::v-deep .dropdown-toggle::after {
  display: none !important;
}

.additional-service-card {
  border-radius: 10px;
  padding: 16px 20px;
  background-color: #f0f4f8;
  border: 1px solid #d1e0e8;
  height: 100%;
}

.dark-layout .additional-service-card {
  background-color: #2e3049;
  border-color: #3a3d5a;
}

.additional-service-icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background-color: #d1e0e8;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.dark-layout .additional-service-icon-wrapper {
  background-color: #3a3d5a;
}

.additional-service-icon {
  color: #00a0df;
}

.dark-layout .additional-service-icon {
  color: #4fc3f7;
}

.additional-service-name {
  font-size: 0.95rem;
  font-weight: 600;
  color: #00a0df;
  line-height: 1.4;
}

.dark-layout .additional-service-name {
  color: #4fc3f7;
}

.additional-service-details {
  font-size: 0.85rem;
  color: #6b7280;
  line-height: 1.6;
}

.dark-layout .additional-service-details {
  color: #b4b7bd;
}

.additional-service-detail-item {
  margin-bottom: 4px;
}

.additional-service-detail-item:last-child {
  margin-bottom: 0;
}

.tracking-chip {
  font-size: 0.75rem;
  font-weight: 500;
  padding: 4px 10px;
}

.dark-layout .tracking-chip {
  color: #ffffff !important;
}

::v-deep .dark-layout .tracking-chip.badge-light-primary {
  color: #ffffff !important;
  background-color: #007bff !important;
}

.dark-layout .tracking-chip .feather-icon {
  color: #ffffff !important;
}

.dark-layout .tracking-chip .text-primary {
  color: #ffffff !important;
}

.tracking-badge-wrapper .feather-icon {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.tracking-badge-wrapper:hover .feather-icon {
  opacity: 1;
}

.package-tabs-wrapper {
  overflow-x: auto;
}

.package-tabs-scroll {
  display: inline-flex;
  gap: 8px;
  white-space: nowrap;
  background-color: #d6d6d63b;
  padding: 6px;
  border-radius: 8px;
}

.dark-layout .package-tabs-scroll {
  background-color: rgba(138, 140, 169, 0.234);
}

.package-tab {
  border-radius: 8px;
  border: 0px solid #e5e7f1;
  background-color: transparent;
  padding: 6px 14px;
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  font-size: 0.85rem;
  color: #4b5563;
  transition: background-color 0.3s ease;
}

.dark-layout .package-tab {
  color: #b4b7bd;
}

.package-tab--active {
  background-color: #00a0df;
  border-color: #00a0df;
  color: #ffffff;
  transition: background-color 0.3s ease;
}

.dark-layout .package-tab--active {
  background-color: #007bff;
  border-color: #007bff;
  color: #ffffff;
}

.package-tab-icon {
  width: 22px;
  height: 22px;
  border-radius: 999px;
  background-color: #ffffff47;
  margin-right: 6px;
}

.package-status-chip {
  border-radius: 999px;
  padding: 3px 12px;
  border: 1px solid #facc6b;
  background-color: #fffaf0;
  font-size: 0.75rem;
  font-weight: 600;
  color: #b45309;
}

.package-tab--active .package-status-chip {
  background-color: #fdb913;
  border-color: #fdb913;
  color: #92400e;
}

.package-items-table {
  background-color: #f5f5f5;
  border-radius: 6px;
  padding: 1px;
}

.dark-layout .package-items-table {
  background-color: #3b4253;
}

.package-items-table .table {
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 0;
  border: none;
  background-color: transparent;
}

.package-items-table-inner {
  border-collapse: separate;
  border-spacing: 0;
}

.package-items-table thead th {
  background-color: #f5f7fb;
  border: none;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
  padding: 12px 16px;
  font-size: 0.875rem;
}

.dark-layout .package-items-table thead th {
  background-color: #2e3049;
  color: #ffffff;
  border-bottom-color: #3a3d5a;
}

.package-items-table tbody td {
  border: none;
  background-color: #ffffff;
  padding: 12px 16px;
  font-size: 0.875rem;
  color: #000000;
  font-weight: 400;
}

.dark-layout .table tbody tr:not([class*='table-']) td {
  background-color: #283046;
  color: #b4b7bd;
}

.package-items-table tbody tr:first-child td {
  border-top: none;
}

.package-items-table tbody tr:last-child td {
  border-bottom: none;
}

.package-info-row .detail-label {
  margin-bottom: 2px;
}

.package-info-row .detail-value {
  font-size: 0.95rem;
}

.movement-row .movement-date {
  min-width: 140px;
}
</style>
